import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Sparkles,
  Zap,
  Users,
  Server,
  Crown,
  ArrowRight,
  Cloud,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Key,
  Copy,
  Clock,
  Building2,
  Lock,
} from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiClient, SubscriptionStatus, Invoice, OnPremLicense, OnPremPricing, cleanErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import ProductSubscriptions from '../components/ProductSubscriptions';
import { PageLoader } from '../components/PageLoader';

interface Plan {
  tier: 'starter' | 'growth' | 'enterprise';
  name: string;
  price: number | 'Custom';
  priceNote?: string;
  description: string;
  features: string[];
  icon: React.ComponentType<{ className?: string }>;
  popular?: boolean;
  maxClusters: number;
  maxCloudAccounts: number;
  cta: string;
  isContactSales?: boolean;
}

// Common features included in all plans
const COMMON_FEATURES = [
  'AI Chat Copilot',
  '24/7 Incident Response',
  'Cloud Infrastructure Map',
];

const PLANS: Plan[] = [
  {
    tier: 'starter',
    name: 'Starter',
    price: 300,
    description: 'Perfect for getting started',
    maxClusters: 1,
    maxCloudAccounts: 1,
    icon: Sparkles,
    cta: 'Get Started',
    features: [
      '1 Kubernetes cluster',
      '1 cloud account',
      'Community support',
    ],
  },
  {
    tier: 'growth',
    name: 'Growth',
    price: 500,
    description: 'Scale across your cloud',
    maxClusters: 5,
    maxCloudAccounts: 5,
    icon: Zap,
    popular: true,
    cta: 'Select Plan',
    priceNote: '1 unit = 1 cluster + 1 cloud account',
    features: [
      'Up to 5 clusters',
      'Up to 5 cloud accounts',
      'Priority support',
    ],
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    description: 'For large organizations',
    maxClusters: -1, // Unlimited
    maxCloudAccounts: -1, // Unlimited
    icon: Crown,
    cta: 'Talk to Sales',
    isContactSales: true,
    features: [
      'Unlimited Kubernetes clusters',
      'Unlimited cloud accounts',
      'On-premise deployment',
      'Dedicated 24/7 support',
    ],
  },
];

export function Billing() {
  const [searchParams] = useSearchParams();
  const { user, isLoading: authLoading } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const INVOICES_PER_PAGE = 5;
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTier, setSelectedTier] = useState<'starter' | 'growth' | 'enterprise'>('starter');
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isStartingSaasFreeTrial, setIsStartingSaasFreeTrial] = useState(false);
  // ProductSubscriptions loads its own data; the page gates on it too so we
  // only ever show a single centered spinner while everything loads.
  const [productSubsLoading, setProductSubsLoading] = useState(true);

  // On-prem specific state
  const [onPremLicense, setOnPremLicense] = useState<OnPremLicense | null>(null);
  const [onPremPricing, setOnPremPricing] = useState<OnPremPricing | null>(null);
  const [onPremUnits, setOnPremUnits] = useState(1);
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [showLicenseKey, setShowLicenseKey] = useState(false);
  const [licenseKeyCopied, setLicenseKeyCopied] = useState(false);

  const isOnPrem = user?.is_onprem_enabled ?? false;

  useEffect(() => {
    // Don't load data until auth is complete to prevent loading wrong data
    // (prevents SaaS billing from briefly showing for on-prem tenants)
    if (authLoading) {
      return;
    }

    if (isOnPrem) {
      loadOnPremData();
    } else {
      loadSubscriptionStatus();
      loadInvoices();
    }
    
    // Check for success/cancelled session
    const sessionId = searchParams.get('session_id');
    const onPremSessionId = searchParams.get('onprem_session_id');
    let checkoutTimer: ReturnType<typeof setTimeout> | undefined;
    if (sessionId || onPremSessionId) {
      // Checkout session completed - refresh status
      checkoutTimer = setTimeout(async () => {
        if (isOnPrem) {
          loadOnPremData();
        } else {
          await loadSubscriptionStatus();
          await loadInvoices();
        }
        // Notify the layout gate so it unblocks pages immediately
        window.dispatchEvent(new Event('subscription-changed'));
      }, 1000);
    }

    return () => {
      if (checkoutTimer) clearTimeout(checkoutTimer);
    };
  }, [searchParams, isOnPrem, authLoading]);

  const loadOnPremData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load pricing and license in parallel
      const [pricing, license] = await Promise.all([
        apiClient.getOnPremPricing(),
        apiClient.getCurrentLicense(true).catch(() => null), // May not exist yet
      ]);
      
      setOnPremPricing(pricing);
      setOnPremLicense(license);
    } catch (err: any) {
      console.error('Failed to load on-prem data:', err);
      setError(cleanErrorMessage(err) || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setIsStartingTrial(true);
      setError('');

      await apiClient.startOnPremTrial({ units: onPremUnits });
      
      // Reload license data
      await loadOnPremData();
      setShowLicenseKey(true);
    } catch (err: any) {
      console.error('Failed to start trial:', err);
      setError(cleanErrorMessage(err) || 'Failed to start trial');
    } finally {
      setIsStartingTrial(false);
    }
  };

  const handleOnPremCheckout = async () => {
    try {
      setIsCreatingCheckout(true);
      setError('');

      const response = await apiClient.createOnPremCheckout({ units: onPremUnits });

      if (response.session_url) {
        window.location.href = response.session_url;
      }
    } catch (err: any) {
      console.error('Failed to create on-prem checkout:', err);
      setError(cleanErrorMessage(err) || 'Failed to start checkout process');
      setIsCreatingCheckout(false);
    }
  };

  const copyLicenseKey = async () => {
    if (onPremLicense?.license_key) {
      await navigator.clipboard.writeText(onPremLicense.license_key);
      setLicenseKeyCopied(true);
      setTimeout(() => setLicenseKeyCopied(false), 2000);
    }
  };

  const calculateOnPremPrice = (units: number) => {
    if (!onPremPricing) return { base: 0, support: 0, total: 0 };
    const base = units * onPremPricing.unit_price_yearly;
    const support = Math.round(base * onPremPricing.support_percentage / 100);
    return { base, support, total: base + support };
  };

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const status = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (err: any) {
      console.error('Failed to load subscription status:', err);
      setError(cleanErrorMessage(err) || 'Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await apiClient.getInvoices();
      setInvoices(response.invoices || []);
    } catch (err: any) {
      console.error('Failed to load invoices:', err);
      // Don't set error for invoices - it's not critical
    } finally {
      setInvoicesLoading(false);
    }
  };

  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);

  const handleSubscribe = async () => {
    try {
      setIsCreatingCheckout(true);
      setError('');

      const plan = PLANS.find(p => p.tier === selectedTier);
      const response = await apiClient.createCheckoutSession({
        tier: selectedTier,
        quantity: plan?.maxClusters === -1 ? 1 : plan?.maxClusters || 1,
      });

      // Use embedded checkout instead of redirect
      if (response.client_secret) {
        setCheckoutClientSecret(response.client_secret);
        setStripePublishableKey(response.publishable_key);
        setIsCreatingCheckout(false);
      } else if (response.session_url) {
        window.location.href = response.session_url;
      }
    } catch (err: any) {
      console.error('Failed to create checkout session:', err);
      setError(cleanErrorMessage(err) || 'Failed to start checkout process');
      setIsCreatingCheckout(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setIsOpeningPortal(true);
      setError('');

      const response = await apiClient.createCustomerPortalSession();

      // Open portal in new tab
      window.open(response.portal_url, '_blank');
    } catch (err: any) {
      console.error('Failed to open customer portal:', err);
      setError(cleanErrorMessage(err) || 'Failed to open billing portal');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleStartSaasFreeTrial = async () => {
    try {
      setIsStartingSaasFreeTrial(true);
      setError('');

      await apiClient.startFreeTrial();

      // Reload subscription status to show the active trial
      await loadSubscriptionStatus();

      // Notify the layout gate so it unblocks pages immediately
      window.dispatchEvent(new Event('subscription-changed'));
    } catch (err: any) {
      console.error('Failed to start free trial:', err);
      setError(cleanErrorMessage(err) || 'Failed to start free trial');
    } finally {
      setIsStartingSaasFreeTrial(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (timestamp: number) => {
    const now = Date.now();
    const end = timestamp * 1000;
    const diff = end - now;
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const getCurrentPlan = () => {
    if (!subscriptionStatus || !subscriptionStatus.tier) return null;
    return PLANS.find(p => p.tier === subscriptionStatus.tier);
  };

  // Wait for auth, billing data, and the ProductSubscriptions section to load
  // before revealing the page, so exactly one centered spinner is shown
  // (prevents multiple staggered spinners and the SaaS-billing flash for
  // on-prem tenants). The content is kept mounted but hidden so
  // ProductSubscriptions can fetch its data during the gate.
  const pageLoading = loading || authLoading || invoicesLoading || productSubsLoading;

  return (
    <>
    {pageLoading && <PageLoader />}
    <div className={pageLoading ? 'hidden' : 'space-y-8'}>
      {/* Success/Error Messages */}
      {(searchParams.get('session_id') || searchParams.get('onprem_session_id')) && (
        <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-300">
            {isOnPrem ? 'Your license is now active!' : 'Thank you for subscribing! Your subscription is now active.'}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="whitespace-pre-wrap">{error}</AlertDescription>
        </Alert>
      )}

      {/* Explainer when arriving from a locked sidebar section */}
      {searchParams.get('product') && (
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            {(() => {
              const productNames: Record<string, string> = {
                workflows: 'Workflows',
                incident_response: 'Incident Response',
                copilot: 'AI Cloud Copilot',
              };
              const name = productNames[searchParams.get('product') || ''] || 'This product';
              return `${name} isn't included in your current plan. Start a free trial or subscribe below to unlock it.`;
            })()}
          </AlertDescription>
        </Alert>
      )}

      {/* Product Subscriptions */}
      <ProductSubscriptions hideInitialLoader onInitialLoad={() => setProductSubsLoading(false)} />

      {/* Embedded Stripe Checkout */}
      {checkoutClientSecret && stripePublishableKey && (
        <Card className="max-w-lg mx-auto">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Complete Payment</CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => { setCheckoutClientSecret(null); setStripePublishableKey(null); }}>
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-hidden rounded-b-lg">
            <div className="max-h-[500px] overflow-y-auto">
              <EmbeddedCheckoutProvider
                stripe={loadStripe(stripePublishableKey)}
                options={{ clientSecret: checkoutClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </CardContent>
        </Card>
      )}

      {/* On-Prem Billing Section */}
      {isOnPrem && (
        <>
          {/* Current License Status */}
          {onPremLicense && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="h-5 w-5" />
                      Current License
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {onPremLicense.license_type === 'trial' ? 'Free Trial License' : 'Paid Annual License'}
                    </CardDescription>
                  </div>
                  <Badge className={
                    onPremLicense.status === 'active' && !onPremLicense.is_in_grace_period
                      ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                      : onPremLicense.is_in_grace_period
                      ? 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800'
                      : 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                  }>
                    {onPremLicense.license_type === 'trial' ? 'TRIAL' : onPremLicense.status.toUpperCase()}
                    {onPremLicense.is_in_grace_period && ' (GRACE PERIOD)'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Units</div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <span className="text-lg font-semibold">{onPremLicense.units}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Clusters</div>
                    <div className="flex items-center gap-2">
                      <Server className="h-5 w-5 text-muted-foreground" />
                      <span className="text-lg font-semibold">{onPremLicense.max_clusters}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Cloud Accounts</div>
                    <div className="flex items-center gap-2">
                      <Cloud className="h-5 w-5 text-muted-foreground" />
                      <span className="text-lg font-semibold">{onPremLicense.max_cloud_accounts}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">
                      {onPremLicense.days_until_expiry >= 0 ? 'Expires In' : 'Expired'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      <span className={`text-lg font-semibold ${onPremLicense.days_until_expiry <= 7 ? 'text-orange-600' : ''}`}>
                        {Math.abs(onPremLicense.days_until_expiry)} days {onPremLicense.days_until_expiry < 0 ? 'ago' : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* License Key Section */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">License Key</Label>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowLicenseKey(!showLicenseKey)}
                      >
                        {showLicenseKey ? 'Hide' : 'Show'}
                        <Eye className="h-4 w-4 ml-1" />
                      </Button>
                      {showLicenseKey && onPremLicense.license_key && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyLicenseKey}
                        >
                          {licenseKeyCopied ? 'Copied!' : 'Copy'}
                          <Copy className="h-4 w-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {showLicenseKey && onPremLicense.license_key && (
                    <pre className="bg-zinc-900 text-zinc-100 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">
                      {onPremLicense.license_key}
                    </pre>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Use this license key in your Kestrel Helm values to activate your on-prem deployment.
                  </p>
                </div>


                {/* Expiration warning */}
                {onPremLicense.days_until_expiry <= 30 && onPremLicense.days_until_expiry > 0 && onPremLicense.license_type === 'paid' && (
                  <Alert className="mt-4 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-700 dark:text-orange-300">
                      Your license expires in {onPremLicense.days_until_expiry} days. Contact us to renew.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* On-Prem Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                On-Premises Pricing
              </CardTitle>
              <CardDescription>
                Deploy Kestrel in your own AWS environment with full control
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Pricing Info */}
                <div className="border rounded-lg p-6 bg-muted/30 flex flex-col">
                  <div className="text-center mb-4">
                    <div className="text-4xl font-bold mb-2">
                      ${onPremPricing?.unit_price_monthly || 300}
                      <span className="text-lg font-normal text-muted-foreground">/mo per unit</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {onPremPricing?.unit_description || '1 Kubernetes cluster + 1 Cloud account'}
                    </p>
                  </div>
                  
                  <div className="text-sm space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span>Billed</span>
                      <span className="font-medium">Annually</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Support & Maintenance</span>
                      <span className="font-medium">{onPremPricing?.support_percentage || 25}% of subscription</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Free Trial</span>
                      <span className="font-medium">{onPremPricing?.trial_days || 14} days</span>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-auto">
                    <h4 className="font-medium mb-3 text-sm">Included in all on-prem licenses:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {['AI Chat Copilot', 'Cloud Incident Response', 'K8s Incident Response', 'Cloud Infrastructure Map'].map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Calculator & Purchase */}
                <div className="border rounded-lg p-6 flex flex-col">
                  <h4 className="font-medium mb-4">Configure Your License</h4>
                  
                  <div className="space-y-4 flex-1">
                    <div>
                      <Label htmlFor="units">Number of Units</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setOnPremUnits(Math.max(1, onPremUnits - 1))}
                          disabled={onPremUnits <= 1}
                        >
                          -
                        </Button>
                        <Input
                          id="units"
                          type="number"
                          min={1}
                          value={onPremUnits}
                          onChange={(e) => setOnPremUnits(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 text-center"
                        />
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setOnPremUnits(onPremUnits + 1)}
                        >
                          +
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          = {onPremUnits} cluster{onPremUnits > 1 ? 's' : ''} + {onPremUnits} cloud account{onPremUnits > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Base ({onPremUnits} units × ${onPremPricing?.unit_price_yearly || 3600}/year)</span>
                        <span>${calculateOnPremPrice(onPremUnits).base.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Support & Maintenance ({onPremPricing?.support_percentage || 25}%)</span>
                        <span>${calculateOnPremPrice(onPremUnits).support.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                        <span>Annual Total</span>
                        <span>${calculateOnPremPrice(onPremUnits).total.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {!onPremLicense ? (
                    <div className="space-y-3 pt-4 mt-auto">
                      <Button
                        onClick={handleStartTrial}
                        disabled={isStartingTrial}
                        className="w-full"
                        variant="outline"
                      >
                        {isStartingTrial ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Starting Trial...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Start {onPremPricing?.trial_days || 14}-Day Free Trial
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleOnPremCheckout}
                        disabled={isCreatingCheckout}
                        className="w-full"
                      >
                        {isCreatingCheckout ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Purchase Annual License
                          </>
                        )}
                      </Button>
                    </div>
                  ) : onPremLicense.license_type === 'paid' ? (
                    <div className="pt-4 mt-auto">
                      <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-700 dark:text-green-300">
                          You have an active paid license. Contact support to modify your units.
                        </AlertDescription>
                      </Alert>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Trial upgrade prompt - shown below the pricing containers */}
              {onPremLicense?.license_type === 'trial' && (
                <div className="mt-6 p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h4 className="font-medium">Upgrade to Paid License</h4>
                      <p className="text-sm text-muted-foreground">
                        Your trial expires on {new Date(onPremLicense.expires_at).toLocaleDateString()}. 
                        Upgrade now to continue using Kestrel with full support.
                      </p>
                    </div>
                    <Button onClick={handleOnPremCheckout} disabled={isCreatingCheckout}>
                      {isCreatingCheckout ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Upgrade to Annual License
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* On-Prem FAQ */}
          <Card>
            <CardHeader>
              <CardTitle>On-Premises FAQ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Collapsible className="border rounded-lg">
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                  <h4 className="font-semibold">How does the free trial work?</h4>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4">
                  <p className="text-sm text-muted-foreground">
                    The free trial gives you {onPremPricing?.trial_days || 14} days to test Kestrel in your environment. 
                    You'll receive a trial license key that will be included in your Helm deployment. 
                    No payment is required to start the trial.
                  </p>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible className="border rounded-lg">
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                  <h4 className="font-semibold">How does license renewal work?</h4>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4">
                  <p className="text-sm text-muted-foreground">
                    You'll receive email reminders 30 days before your license expires. 
                    After renewal payment, you'll receive a new license key to update in your deployment. 
                    There's a 7-day grace period after expiration during which the system continues to work.
                  </p>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible className="border rounded-lg">
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                  <h4 className="font-semibold">What's included in the support fee?</h4>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4">
                  <p className="text-sm text-muted-foreground">
                    The {onPremPricing?.support_percentage || 25}% support fee includes 24/7 slack/email support, 
                    access to software updates, and assistance with deployment issues.
                  </p>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible className="border rounded-lg">
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
                  <h4 className="font-semibold">Can I use the license in air-gapped environments?</h4>
                  <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-4 pb-4">
                  <p className="text-sm text-muted-foreground">
                    Yes! The license key is self-contained and doesn't require any internet connectivity for validation. 
                    It's validated locally by the Kestrel server, making it compatible with air-gapped deployments.
                  </p>
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          </Card>

          {/* Contact Support */}
          <Card className="bg-muted/50">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">Need help with your on-prem deployment?</h3>
                  <p className="text-sm text-muted-foreground">
                    Our team is here to help you with deployment, configuration, and troubleshooting.
                  </p>
                </div>
                <Button
                  onClick={() => window.open('mailto:hello@usekestrel.ai?subject=On-Prem Support', '_blank')}
                  variant="outline"
                  className="gap-2"
                >
                  <Users className="h-4 w-4" />
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Legacy SaaS Billing Section — only shown for customers with existing tier subscriptions */}
      {!isOnPrem && subscriptionStatus?.has_active_subscription && subscriptionStatus?.tier && !subscriptionStatus?.is_product_subscription && (
        <>
      {/* Current Subscription Status */}
      {subscriptionStatus?.has_active_subscription && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle>
                  {subscriptionStatus.is_free_trial ? 'Free Trial' : 'Current Subscription'}
                </CardTitle>
                <CardDescription className="mt-2">
                  {subscriptionStatus.is_free_trial
                    ? <>You're on a <strong>14-day free trial</strong> with Starter plan access</>
                    : <>You're subscribed to the <strong>{getCurrentPlan()?.name || subscriptionStatus.tier}</strong> plan</>
                  }
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Plan</div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const currentPlan = getCurrentPlan();
                    const IconComponent = currentPlan?.icon;
                    return IconComponent ? <IconComponent className="h-5 w-5 text-primary" /> : null;
                  })()}
                  <span className="text-lg font-semibold">{getCurrentPlan()?.name || subscriptionStatus.tier}</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Clusters</div>
                <div className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-muted-foreground" />
                  <span className="text-lg font-semibold">
                    {subscriptionStatus.clusters_used} / {subscriptionStatus.clusters_limit === -1 ? 'Unlimited' : subscriptionStatus.clusters_limit}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Cloud Accounts</div>
                <div className="flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-muted-foreground" />
                  <span className="text-lg font-semibold">
                    {subscriptionStatus.cloud_accounts_used} / {subscriptionStatus.cloud_accounts_limit === -1 ? 'Unlimited' : subscriptionStatus.cloud_accounts_limit}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Status</div>
                <Badge className={
                  subscriptionStatus.status === 'active' 
                    ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                    : subscriptionStatus.status === 'trialing'
                    ? 'bg-primary text-primary-foreground border-0'
                    : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800'
                }>
                  {subscriptionStatus.status === 'trialing' ? 'FREE TRIAL' : subscriptionStatus.status?.toUpperCase() || 'ACTIVE'}
                </Badge>
              </div>
            </div>
            
            {subscriptionStatus.current_period_end && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {subscriptionStatus.status === 'trialing' 
                      ? 'Trial ends on'
                      : subscriptionStatus.cancel_at_period_end 
                      ? 'Cancels on' 
                      : 'Renews on'}
                  </span>
                  <span className="font-medium">
                    {formatDate(subscriptionStatus.current_period_end)}
                    {subscriptionStatus.status === 'trialing' && subscriptionStatus.is_free_trial && (
                      <span className="text-primary ml-2">
                        ({getDaysRemaining(subscriptionStatus.current_period_end)} days left)
                      </span>
                    )}
                  </span>
                </div>
                {subscriptionStatus.status === 'trialing' && subscriptionStatus.is_free_trial && (
                  <Alert className="mt-3 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700 dark:text-green-300">
                      You're on a free trial! No credit card required. You have {getDaysRemaining(subscriptionStatus.current_period_end)} days remaining. Subscribe to a paid plan to continue after the trial.
                    </AlertDescription>
                  </Alert>
                )}
                {subscriptionStatus.status === 'trialing' && !subscriptionStatus.is_free_trial && (
                  <Alert className="mt-3 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700 dark:text-green-300">
                      You're on a free trial! Your card will be charged on {formatDate(subscriptionStatus.current_period_end)} unless you cancel.
                    </AlertDescription>
                  </Alert>
                )}
                {subscriptionStatus.cancel_at_period_end && (
                  <Alert className="mt-3 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-700 dark:text-orange-300">
                      Your subscription is scheduled to cancel. You can reactivate it from the billing portal.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {subscriptionStatus.is_free_trial ? (
              <div className="mt-4">
                <p className="text-xs text-center text-muted-foreground">
                  Subscribe to a paid plan below to continue after your free trial ends.
                </p>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  onClick={handleManageSubscription}
                  disabled={isOpeningPortal}
                  className="w-full gap-2"
                >
                  {isOpeningPortal ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  {subscriptionStatus.quantity && subscriptionStatus.quantity > 0 
                    ? 'Manage Subscription & Update Quantities'
                    : 'Manage Subscription'}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Update payment method, change cluster/cloud account quantities, or cancel subscription
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-2">
          {subscriptionStatus?.has_active_subscription && !subscriptionStatus?.is_free_trial
            ? 'Change Plan'
            : subscriptionStatus?.is_free_trial
            ? 'Subscribe to a Plan'
            : 'Choose Your Plan'}
        </h2>
        <p className="text-muted-foreground text-sm mb-4">
          {subscriptionStatus?.has_active_subscription && !subscriptionStatus?.is_free_trial
            ? 'Switch to a different plan that better fits your needs'
            : subscriptionStatus?.is_free_trial
            ? 'Convert your free trial to a paid subscription to keep using Kestrel after your trial ends'
            : 'Scale from single accounts to enterprise cloud estates'}
        </p>

        {/* Common Features */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
          {COMMON_FEATURES.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const isCurrentPlan = subscriptionStatus?.tier === plan.tier;
            
            return (
              <Card 
                key={plan.tier}
                className={`relative flex flex-col ${
                  isCurrentPlan
                    ? 'border-2 border-foreground'
                    : ''
                }`}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge variant="default">
                      Current Plan
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="mt-1">{plan.description}</CardDescription>
                  <div className="mt-4">
                    <div className="flex items-baseline justify-center gap-1">
                      {plan.price === 'Custom' ? (
                        <span className="text-3xl font-bold">Custom</span>
                      ) : (
                        <>
                          <span className="text-3xl font-bold">${plan.price}</span>
                          <span className="text-muted-foreground text-sm">{plan.tier === 'growth' ? '/mo per unit' : '/mo'}</span>
                        </>
                      )}
                    </div>
                    {plan.priceNote && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {plan.priceNote}
                      </p>
                    )}
                    {!subscriptionStatus?.has_active_subscription && plan.tier === 'starter' && (
                      <p className="text-xs text-muted-foreground font-medium mt-2">
                        Try free for 14 days - no credit card needed
                      </p>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col">
                  {/* Features List */}
                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  <div className="mt-auto">
                  {plan.isContactSales ? (
                    <Button
                      onClick={() => window.open('mailto:hello@usekestrel.ai?subject=Enterprise Plan Inquiry', '_blank')}
                      className="w-full"
                      variant="outline"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Talk to Sales
                    </Button>
                  ) : !subscriptionStatus?.has_active_subscription || subscriptionStatus?.is_free_trial ? (
                    <div className="space-y-2">
                      {/* Free Trial button for Starter tier only — only when no subscription/trial at all */}
                      {plan.tier === 'starter' && !subscriptionStatus?.has_active_subscription && (
                        <Button
                          onClick={handleStartSaasFreeTrial}
                          disabled={isStartingSaasFreeTrial}
                          className="w-full"
                        >
                          {isStartingSaasFreeTrial ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Starting Trial...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Start 14-Day Free Trial
                            </>
                          )}
                        </Button>
                      )}
                      {/* Subscribe button (goes to Stripe checkout for paid subscription) */}
                      <Button
                        onClick={() => {
                          setSelectedTier(plan.tier);
                          if (selectedTier === plan.tier) {
                            handleSubscribe();
                          }
                        }}
                        disabled={isCreatingCheckout}
                        className="w-full"
                        variant="outline"
                      >
                        {isCreatingCheckout && selectedTier === plan.tier ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : selectedTier === plan.tier ? (
                          <>
                            {plan.cta}
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </>
                        ) : (
                          plan.cta
                        )}
                      </Button>
                    </div>
                  ) : isCurrentPlan ? (
                    <Button
                      onClick={handleManageSubscription}
                      disabled={isOpeningPortal}
                      className="w-full"
                      variant="outline"
                    >
                      {isOpeningPortal ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      Manage This Plan
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setSelectedTier(plan.tier);
                        if (selectedTier === plan.tier) {
                          handleSubscribe();
                        }
                      }}
                      disabled={isCreatingCheckout}
                      className="w-full"
                      variant="outline"
                    >
                      {isCreatingCheckout && selectedTier === plan.tier ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Switch to {plan.name}
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>


      {/* FAQ Section */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently Asked Questions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Collapsible className="border rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <h4 className="font-semibold">How does the free trial work?</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-sm text-muted-foreground">
                The Starter plan includes a 14-day free trial — no credit card required. You'll get access to
                1 Kubernetes cluster and 1 cloud account during the trial. After the 14 days, you'll need to
                subscribe to a paid plan to continue using the platform.
              </p>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible className="border rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <h4 className="font-semibold">What's included in all plans?</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-sm text-muted-foreground">
                All plans include our AI Chat Copilot, 24/7 Incident Response, and Cloud Infrastructure Map.
                These core features help you monitor your cloud infrastructure and respond to incidents effectively.
              </p>
            </CollapsibleContent>
          </Collapsible>
          
          <Collapsible className="border rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <h4 className="font-semibold">What are the limits for each plan?</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-sm text-muted-foreground">
                Starter supports 1 Kubernetes cluster and 1 cloud account. Growth is $500/mo per unit, where each unit gives you 1 cluster and 1 cloud account (up to 5 units max).
                Enterprise has unlimited clusters and cloud accounts.
              </p>
            </CollapsibleContent>
          </Collapsible>
          
          <Collapsible className="border rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <h4 className="font-semibold">Can I change my plan later?</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-sm text-muted-foreground">
                Yes! You can upgrade your plan at any time. Contact us if you need to change your plan.
              </p>
            </CollapsibleContent>
          </Collapsible>
          
          <Collapsible className="border rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <h4 className="font-semibold">What payment methods do you accept?</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-sm text-muted-foreground">
                We accept all major credit cards, debit cards, and ACH bank transfers through Stripe.
                All payment information is securely processed and stored by Stripe - we never see your card details.
              </p>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible className="border rounded-lg">
            <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left rounded-lg hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <h4 className="font-semibold">How does Growth pricing work?</h4>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4">
              <p className="text-sm text-muted-foreground">
                Growth is priced at $500/month per unit. Each unit gives you 1 Kubernetes cluster and 1 cloud account.
                You can purchase up to 5 units ($2,500/mo for 5 clusters + 5 cloud accounts). For Enterprise or custom needs, contact hello@usekestrel.ai.
              </p>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="bg-muted/50">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold mb-1">Need help choosing a plan?</h3>
              <p className="text-sm text-muted-foreground">
                Our team is here to help you find the right solution for your organization.
              </p>
            </div>
            <Button
              onClick={() => window.open('mailto:hello@usekestrel.ai?subject=Subscription Help', '_blank')}
              variant="outline"
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              Contact Sales
            </Button>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      {/* Invoices Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            Invoices
          </CardTitle>
          <CardDescription>
            View and download your billing invoices
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No invoices yet</p>
              <p className="text-sm">Invoices will appear here after your first billing cycle</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices
                    .slice((invoicesPage - 1) * INVOICES_PER_PAGE, invoicesPage * INVOICES_PER_PAGE)
                    .map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.number || invoice.id}</TableCell>
                      <TableCell>
                        {new Date(invoice.created * 1000).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.period_start * 1000).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                        {' - '}
                        {new Date(invoice.period_end * 1000).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </TableCell>
                      <TableCell>
                        ${(invoice.amount_paid / 100).toFixed(2)} {invoice.currency.toUpperCase()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            invoice.status === 'paid'
                              ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                              : invoice.status === 'open'
                              ? 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800'
                              : 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800'
                          }
                        >
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {invoice.hosted_invoice_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                              className="gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </Button>
                          )}
                          {invoice.invoice_pdf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(invoice.invoice_pdf, '_blank')}
                              className="gap-1"
                            >
                              <Download className="h-4 w-4" />
                              PDF
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {invoices.length > INVOICES_PER_PAGE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((invoicesPage - 1) * INVOICES_PER_PAGE) + 1} to {Math.min(invoicesPage * INVOICES_PER_PAGE, invoices.length)} of {invoices.length} invoices
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setInvoicesPage(p => Math.max(1, p - 1))}
                      disabled={invoicesPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {invoicesPage} of {Math.ceil(invoices.length / INVOICES_PER_PAGE)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setInvoicesPage(p => Math.min(Math.ceil(invoices.length / INVOICES_PER_PAGE), p + 1))}
                      disabled={invoicesPage >= Math.ceil(invoices.length / INVOICES_PER_PAGE)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

    </div>
    </>
  );
}

export default Billing;

